import { Fragment, type ReactNode } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation, type Location } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthProvider.js';
import { HomePage } from './pages/HomePage.js';
import { StatPage } from './pages/stats/StatPage.js';
import { MonthDetailPage } from './pages/stats/MonthDetailPage.js';
import { BoxReportPage } from './pages/stats/BoxReportPage.js';
import { ZaikoPage } from './pages/randombox/ZaikoPage.js';
import { LookupPage } from './pages/lookup/LookupPage.js';
import { FreepassPage } from './pages/tokens/FreepassPage.js';
import { MemberOutCheckPage } from './pages/site/MemberOutCheckPage.js';
import { JoinDashboardPage } from './pages/joinstats/JoinDashboardPage.js';
import { JoinWeekdayPage } from './pages/joinstats/JoinWeekdayPage.js';
import { SalesPage } from './pages/xadm/SalesPage.js';
import { VisitsPage } from './pages/xadm/VisitsPage.js';
import { OrdersPage as XadmOrdersPage } from './pages/xadm/OrdersPage.js';
import { MembersPage } from './pages/xadm/MembersPage.js';
import { LogisticsPage } from './pages/xadm/LogisticsPage.js';
import { LogisticsCounterPage } from './pages/xadm/LogisticsCounterPage.js';
import { AdwordsPage } from './pages/xadm/AdwordsPage.js';
import { CsReportPage } from './pages/xadm/CsReportPage.js';
import { NetworkReportPage } from './pages/network/NetworkReportPage.js';
import { RefererListPage } from './pages/network/RefererListPage.js';
import { RefererStatsPage } from './pages/network/RefererStatsPage.js';
import { AdSitesPage } from './pages/network/AdSitesPage.js';
import { AdAccountsPage } from './pages/network/AdAccountsPage.js';
import { ImportPage } from './pages/imports/ImportPage.js';
import { EmailExportPage } from './pages/exports/EmailExportPage.js';
import { OutmemberEmailPage } from './pages/marketing/OutmemberEmailPage.js';
import { TimetablePage } from './pages/offline/TimetablePage.js';
import { OfflineSalesPage } from './pages/offline/SalesPage.js';
import { InvenSyncPage } from './pages/offline/InvenSyncPage.js';
import { BankPage } from './pages/payment/BankPage.js';
import { CardPage } from './pages/payment/CardPage.js';
import { RecurringPage } from './pages/payment/RecurringPage.js';
import { BankCheckPage } from './pages/payment/BankCheckPage.js';
import { BankIncomeCheckPage } from './pages/payment/BankIncomeCheckPage.js';
import { BankExcelCheckPage } from './pages/payment/BankExcelCheckPage.js';
import { Layout } from './components/Layout.js';
import { Spinner } from './components/ui.js';
import { LoginPage } from './pages/LoginPage.js';
import { IpBlockedPage } from './pages/IpBlockedPage.js';
import { ChangePasswordPage } from './pages/ChangePasswordPage.js';
import { AdminAccountsPage } from './pages/AdminAccountsPage.js';
import { WhitelistPage } from './pages/WhitelistPage.js';
import { ShippingFeesPage } from './pages/randombox/ShippingFeesPage.js';
import { RedisStatusPage } from './pages/serverCheck/RedisStatusPage.js';
import { BatchAdminPage } from './pages/serverCheck/BatchAdminPage.js';
import { SmsLogPage } from './pages/serverCheck/SmsLogPage.js';
import { OrdersPage } from './pages/lottery/OrdersPage.js';
import { TrackingPage } from './pages/lottery/TrackingPage.js';
import { OrderDetailPage } from './pages/lottery/OrderDetailPage.js';
import { OrderDetailModal } from './pages/lottery/OrderDetailModal.js';
import { ProductsPage } from './pages/lottery/ProductsPage.js';
import { PrizesPage } from './pages/lottery/PrizesPage.js';
import { CategoriesPage } from './pages/lottery/CategoriesPage.js';
import { RbOrdersPage } from './pages/randombox/OrdersPage.js';
import { RbTrackingPage } from './pages/randombox/TrackingPage.js';
import { RbOrderDetailPage } from './pages/randombox/OrderDetailPage.js';
import { RbOrderDetailModal } from './pages/randombox/OrderDetailModal.js';
import { RbProductsPage } from './pages/randombox/ProductsPage.js';
import { RbPrizesPage } from './pages/randombox/PrizesPage.js';
import { RbCategoriesPage } from './pages/randombox/CategoriesPage.js';
import { FzProductsPage } from './pages/fanza/ProductsPage.js';
import { FzPrizesPage } from './pages/fanza/PrizesPage.js';
import { ReportPage } from './pages/reports/ReportPage.js';
import { RandomboxPrizeRankingPage } from './pages/reports/RandomboxPrizeRankingPage.js';
import { ZaikoStockPage } from './pages/reports/ZaikoStockPage.js';
import { SalesAnalysisPage } from './pages/reports/SalesAnalysisPage.js';
import { ReviewBoardPage } from './pages/reports/ReviewBoardPage.js';
import { CateBestPage } from './pages/reports/CateBestPage.js';
import { EventListPage } from './pages/reports/EventListPage.js';
import { OfflineCatePage } from './pages/reports/OfflineCatePage.js';
import { PartnerLandingPage } from './pages/reports/PartnerLandingPage.js';
import { ItemOrdersLogPage } from './pages/reports/ItemOrdersLogPage.js';
import { AdwordsStatusPage } from './pages/xadm/AdwordsStatusPage.js';
import { PartnersReportPage } from './pages/reports/PartnersReportPage.js';
import { ProductRankPage } from './pages/reports/ProductRankPage.js';
import { CategoryRankPage } from './pages/reports/CategoryRankPage.js';
import { ApiError } from './api/client.js';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // 4xx 는 재시도해도 결과가 같다. 네트워크/5xx 만 두 번까지.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
  },
});

/**
 * 라우트 파라미터가 바뀌면 안쪽을 새로 마운트한다.
 *
 * /reports/:reportId 는 Route 가 하나라서 reportId 만 바뀌면 React 가
 * ReportPage 인스턴스를 그대로 재사용한다. 그래서 A 리포트에 친 검색어가
 * B 리포트에 남고, 조회도구에서는 이전 도구의 결과가 남은 채로 다음 도구의
 * 모양으로 그려지다 화면이 깨졌다.
 *
 * 화면마다 useEffect 로 상태를 지우는 대신 여기서 한 번에 끊는다 - 새로 추가되는
 * 파라미터 화면도 같은 규칙을 따르게 하려는 것이다.
 */
export function KeyedByParam({ name, children }: { name: string; children: ReactNode }) {
  const params = useParams();
  return <Fragment key={params[name] ?? ''}>{children}</Fragment>;
}

export function ProtectedRoutes() {
  const { user, isLoading, ipBlocked } = useAuth();
  const location = useLocation();
  // 목록에서 상세를 열 때 <Link state={{ background: location }}> 로 "뒤에 깔린
  // 화면"을 함께 넘긴다. 그 상태가 있으면 메인 <Routes> 는 그 배경 위치로 그려
  // 목록이 그대로 남아 있게 하고, 상세는 아래의 두 번째 <Routes> 가 겹쳐 그린다.
  const background = (location.state as { background?: Location } | null)?.background;

  if (isLoading) return <Spinner label="세션 확인 중…" />;
  // 화이트리스트에 없는 IP - 로그인 폼을 보여줘 봤자 눌러도 똑같이 막히므로,
  // 시도하기 전에 바로 알려준다 (AuthProvider.tsx 참고).
  if (ipBlocked) return <IpBlockedPage />;
  if (!user) return <LoginPage />;
  // xadmin_account 로 등록한 계정이 아직 초기 비밀번호 그대로면, 바꾸기 전엔
  // 어느 경로로 들어왔든 이 화면만 보여준다 (server 도 같은 걸 한 번 더
  // 막는다 - middleware/requirePasswordChanged.ts).
  if (user.mustChangePassword) return <ChangePasswordPage />;

  return (
    <>
      <Routes location={background ?? location}>
        <Route element={<Layout />}>
          <Route index element={<HomePage />} />
          <Route path="/lottery/orders" element={<OrdersPage />} />
          <Route path="/lottery/tracking" element={<TrackingPage />} />
          <Route path="/lottery/orders/:orderNo" element={<OrderDetailPage />} />
          <Route path="/lottery/products" element={<ProductsPage />} />
          <Route path="/lottery/products/:lotteryId/prizes" element={<PrizesPage />} />
          <Route path="/lottery/categories" element={<CategoriesPage />} />

          <Route path="/randombox/orders" element={<RbOrdersPage />} />
          <Route path="/randombox/tracking" element={<RbTrackingPage />} />
          <Route path="/randombox/orders/:orderNo" element={<RbOrderDetailPage />} />
          <Route path="/randombox/products" element={<RbProductsPage />} />
          <Route path="/randombox/products/:randomboxId/prizes" element={<RbPrizesPage />} />
          <Route path="/randombox/categories" element={<RbCategoriesPage />} />

          <Route path="/fanza/products" element={<FzProductsPage />} />
          <Route path="/fanza/products/:randomboxId/prizes" element={<KeyedByParam name="randomboxId"><FzPrizesPage /></KeyedByParam>} />

          {/* 전용 화면으로 옮긴 리포트. /reports/:reportId 보다 더 구체적이라 먼저 잡힌다. */}
          {/* 원본 partner.bananamall.co.kr/util/partners_report.php (메뉴 '도메인 모니터링').
              레거시 주소를 그대로 옮긴 경로라 이 화면만 밑줄(_)을 쓴다. */}
          <Route path="/reports/partners_report" element={<PartnersReportPage />} />
          <Route path="/reports/payment-bank" element={<BankPage />} />
          <Route path="/reports/payment-card" element={<CardPage />} />
          <Route path="/reports/payment-recurring" element={<RecurringPage />} />
          <Route path="/reports/randombox-prize-ranking" element={<RandomboxPrizeRankingPage />} />
          <Route path="/reports/zaiko-stock" element={<ZaikoStockPage />} />
          <Route path="/reports/sales-analysis" element={<SalesAnalysisPage />} />
          <Route path="/reports/review-board" element={<ReviewBoardPage />} />
          <Route path="/reports/cate-best" element={<CateBestPage />} />
          <Route path="/reports/event-list" element={<EventListPage />} />
          <Route path="/reports/offline-cate" element={<OfflineCatePage />} />
          <Route path="/reports/partner-landing" element={<PartnerLandingPage />} />
          <Route path="/reports/item-orders-log" element={<ItemOrdersLogPage />} />
          <Route path="/reports/product-rank" element={<ProductRankPage />} />
          <Route path="/reports/category-rank" element={<CategoryRankPage />} />
          <Route path="/payment/bank/check" element={<BankCheckPage />} />
          <Route path="/payment/bank/income-check" element={<BankIncomeCheckPage />} />
          <Route path="/payment/bank/excel-check" element={<BankExcelCheckPage />} />

          {/* 정의 기반 조회 화면. 서버의 report.registry.ts 에 정의를 늘리면 여기로 잡힌다. */}
          <Route path="/reports/:reportId" element={<KeyedByParam name="reportId"><ReportPage /></KeyedByParam>} />

          <Route path="/stats/lottery" element={<StatPage key="lottery" kind="lottery" />} />
          <Route path="/stats/randombox" element={<StatPage key="randombox" kind="randombox" />} />
          <Route path="/stats/lottery/month" element={<MonthDetailPage />} />
          <Route path="/stats/lottery/box" element={<BoxReportPage key="lottery" kind="lottery" />} />
          <Route path="/stats/randombox/box" element={<BoxReportPage key="randombox" kind="randombox" />} />
          <Route path="/randombox/zaiko" element={<ZaikoPage />} />
          <Route path="/randombox/shipping-fees" element={<ShippingFeesPage />} />
          <Route path="/lookup/:lookupId" element={<KeyedByParam name="lookupId"><LookupPage /></KeyedByParam>} />
          <Route path="/tokens/freepass" element={<FreepassPage />} />
          <Route path="/site/member-out-check" element={<MemberOutCheckPage />} />
          <Route path="/join-stats" element={<JoinDashboardPage />} />
          <Route path="/join-stats/weekday" element={<JoinWeekdayPage />} />
          <Route path="/xadm/sales" element={<SalesPage />} />
          <Route path="/xadm/visits" element={<VisitsPage />} />
          <Route path="/xadm/orders" element={<XadmOrdersPage />} />
          <Route path="/xadm/members" element={<MembersPage />} />
          <Route path="/logistics" element={<LogisticsPage />} />
          {/* 애드워즈는 같은 자료를 보는 화면이 둘이다 (menu.ts '통계' 참고):
              /xadm/adwords 가 원본 report_adw.php 의 표 그대로이고,
              /xadm/adwords-performance 가 그것을 요약·추세로 다시 그린 화면이다. */}
          <Route path="/xadm/adwords" element={<AdwordsStatusPage />} />
          <Route path="/xadm/adwords-performance" element={<AdwordsPage />} />
          <Route path="/xadm/cs-report" element={<CsReportPage />} />
          {/* 원본 /xadm/report_network_test_frame(_main).php · report_network.php. */}
          <Route path="/network/report" element={<NetworkReportPage />} />
          {/* 원본 /xadm/report_network_referer*.php · report_network_adinfo.php (읽기 전용). */}
          <Route path="/network/referers" element={<RefererListPage />} />
          <Route path="/network/referer-stats" element={<RefererStatsPage />} />
          <Route path="/network/sites" element={<AdSitesPage />} />
          <Route path="/network/adinfo" element={<AdAccountsPage />} />
          <Route path="/imports/ga" element={<ImportPage key="ga" kind="ga" />} />
          <Route path="/imports/domains" element={<ImportPage key="domains" kind="domains" />} />
          <Route path="/exports/emails" element={<EmailExportPage />} />
          {/* 원본 ../xadmin/marketing/outmember_email.php - 레거시 주소를 그대로
              옮긴 경로라 이 화면도 밑줄(_)을 쓴다(위 partners_report 와 같은 이유). */}
          <Route path="/marketing/outmember_email" element={<OutmemberEmailPage />} />
          <Route path="/offline/timetable" element={<TimetablePage />} />
          <Route path="/offline/sales" element={<OfflineSalesPage />} />
          <Route path="/offline/inven-sync" element={<InvenSyncPage />} />
          {/* menu.ts '설정' 섹션(관리자 전용)과 Layout 상단의 admin 전용 바로가기,
              둘 다에서 이 경로로 들어온다. */}
          <Route path="/admin-accounts" element={<AdminAccountsPage />} />
          {/* 원본 xadmin/whiteip_list.php. menu.ts '설정' 섹션으로만 들어온다. */}
          <Route path="/whitelist" element={<WhitelistPage />} />
          {/* 서버체크 섹션 (2026-09-05, Redis 를 새로 설치하며 요청). */}
          <Route path="/server-check/redis" element={<RedisStatusPage />} />
          <Route path="/server-check/batch" element={<BatchAdminPage />} />
          <Route path="/server-check/sms-log" element={<SmsLogPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>

      {/* background 가 있을 때만 - 즉 목록에서 열었을 때만 - 위에 겹쳐 그린다. */}
      {background && (
        <Routes>
          <Route path="/lottery/orders/:orderNo" element={<OrderDetailModal />} />
          <Route path="/randombox/orders/:orderNo" element={<RbOrderDetailModal />} />
        </Routes>
      )}
    </>
  );
}

/**
 * 배송현황판은 라즈베리파이 Chromium 키오스크에서 상시 표시한다. 이 경로만
 * AuthProvider/ProtectedRoutes/Layout 바깥에 둬서 세션 확인 요청과 관리자 메뉴를
 * 만들지 않는다. 나머지 모든 화면은 기존 로그인 게이트를 그대로 거친다.
 */
export function AppRoutes() {
  const { pathname } = useLocation();
  if (pathname === '/logistics/counter' || pathname === '/logistics/counter/') {
    return <LogisticsCounterPage />;
  }
  return (
    <AuthProvider>
      <ProtectedRoutes />
    </AuthProvider>
  );
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </QueryClientProvider>
  );
}
